import React, { useRef } from "react";
import {
  AddressBookHandlerProvider,
  useAddressBookHandlerContext,
} from "./providers/index.ts";
import { useAppContext } from "../../AppProvider.jsx";
import InputIcon from "../../Components/Inputs/InputIcon/InputIcon.tsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getFieldError } from "../../utilities/utilities.ts";
import {
  faAddressBook,
  faAt,
  faCity,
  faICursor,
  faInfoCircle,
  faLoader,
  faPhoneAlt,
  faUser,
} from "../../utilities/fontawesomeIcons.js";
import { AddressBook } from "../../Models/AddressBook.ts";
import Input from "../../Components/Inputs/Input/Input.tsx";
import ButtonConfirm from "../../Components/Buttons/Confirm.tsx";
import { Modal, ModalBody, UncontrolledTooltip } from "reactstrap";
import AsyncSelect from "../../Components/Selects/AsyncSelect/AsyncSelect.tsx";
import { FetchParam } from "../../Models/FetchParam.ts";
import { getAllRegistryAddressBook } from "../../Components/Selects/AsyncSelect/api.ts";
import { createModalConfig } from "../../Models/ModalConfig.ts";
import ButtonUtility from "../../Components/Buttons/Utility.tsx";
import { Checkbox } from "../../Components/Checkboxs/Checkbox/Checkbox.tsx";
import { LocationHandler } from "../Locations/LocationHandler.tsx";
import { Location } from "../../Models/Location.ts";
import { LocationHandlerRef } from "../Locations/providers/types.ts";

export const AddressBookHandler = () => {
  const { dictionary } = useAppContext();
  const {
    service,
    data,
    error,
    loader,
    action,
    label,
    billing,
    registry,
    autosave,
    ownerId,
    modalConfig,
    billingAddressBookIsDifferentFromTheAddressBook,
    searchInAddressBookParams,
    callback,
    saveAddressBook,
    saveRegistryAddressBook,
    setBillingAddressBookIsDifferentFromTheAddressBook,
    setModalConfig,
  } = useAddressBookHandlerContext();

  const locationSearchRef = useRef<LocationHandlerRef>(null);

  const handleChange = (
    name: string,
    value: string | boolean | number | object | null,
    key: string,
  ) => {
    if (action !== "details") {
      saveAddressBook(
        {
          ...data,
          [name]: value,
        } as AddressBook,
        key as keyof AddressBook,
        autosave,
      );
    }
  };

  return (
    <React.Fragment>
      <div className="row">
        <div className="col">
          {loader ? (
            <div className="row">
              <div className="col text-center">
                <FontAwesomeIcon icon={faLoader} spin size="2x" />
              </div>
            </div>
          ) : (
            <React.Fragment>
              <div className="row align-items-center mb-2">
                <div className="col-9 col-md-5 col-xl-6 col-xxl-7">
                  {label && <h3 className="m-0">{label}</h3>}
                </div>
                {action !== "details" && registry?.searchEnabled && (
                  <div className="col-3 col-md-7 col-xl-6 col-xxl-5 text-end">
                    <span
                      className="span-1 cursor-pointer text-decoration-underline"
                      onClick={() => {
                        setModalConfig({
                          isOpen: true,
                          action: "searchInAddressBook",
                          size: "md",
                          data: [data.locationId],
                          title: dictionary.contacts.destinations_address_book,
                        });
                      }}
                      title={dictionary.contacts.search_address_in_address_book}
                    >
                      <FontAwesomeIcon icon={faAddressBook} />{" "}
                      {dictionary.contacts.search_address_in_address_book}
                    </span>
                  </div>
                )}
              </div>
              <div className="row gx-2">
                <div className="col mb-1">
                  <Checkbox
                    name="priv"
                    checked={data.priv || false}
                    onChange={({ target }) => {
                      handleChange(target.name, !data.priv, "priv");
                    }}
                    disabled={action === "details"}
                    label={dictionary.words.private}
                    float="end"
                  />
                </div>
              </div>
              <div className="row gx-2">
                <div className="col mb-1">
                  <label className="label-2">
                    {dictionary.registries.name} {dictionary.words.and}{" "}
                    {dictionary.users.surname}{" "}
                    <FontAwesomeIcon
                      id={"nameAndSurnameInfo"}
                      icon={faInfoCircle}
                      className="cursor-pointer"
                    />
                    <UncontrolledTooltip target={"nameAndSurnameInfo"}>
                      {dictionary.messages.name_and_surname_on_intercom}
                    </UncontrolledTooltip>
                  </label>
                  <InputIcon
                    name="contactName"
                    value={data.contactName || ""}
                    type="text"
                    placeholder={action !== "details" ? "Mario Rossi" : ""}
                    onChange={({ target }) => {
                      handleChange(target.name, target.value, "contactName");
                    }}
                    icon={faUser}
                    disabled={action === "details"}
                    error={getFieldError(error, "contactName")}
                  />
                </div>
                <div className="col-12 col-md-5 mb-1">
                  <label className="label-2">
                    {dictionary.references.customer_reference}
                  </label>
                  <InputIcon
                    name="externalReference"
                    value={data.externalReference || ""}
                    type="text"
                    placeholder="EXTREF"
                    onChange={({ target }) => {
                      handleChange(
                        target.name,
                        target.value,
                        "externalReference",
                      );
                    }}
                    icon={faICursor}
                    disabled={action === "details"}
                    error={getFieldError(error, "externalReference")}
                  />
                </div>
              </div>
              <div className="row gx-2">
                <div className="col">
                  <label className="label-2">
                    {dictionary.locations.address}
                  </label>
                  <LocationHandler
                    ref={locationSearchRef}
                    id={data.location?.id}
                    name="location"
                    action={action}
                    autosave={autosave}
                    isDisabled={action === "details"}
                    service={service}
                    className="mb-1"
                    callback={(location) => {
                      if (action !== "details") {
                        saveAddressBook(
                          {
                            ...data,
                            location: {
                              ...data.location,
                              ...location,
                            },
                          } as AddressBook,
                          "location" as keyof AddressBook,
                          autosave,
                        );
                      }
                    }}
                    error={getFieldError(error, "location")}
                  />
                </div>
              </div>
              {data.priv === false && (
                <div className="row gx-2">
                  <div className="col-12 mb-1">
                    <label className="label-2">
                      {dictionary.registries.business_name}{" "}
                      <FontAwesomeIcon
                        id={"businessNameInfo"}
                        icon={faInfoCircle}
                        className="cursor-pointer"
                      />
                      <UncontrolledTooltip target={"businessNameInfo"}>
                        {dictionary.messages.business_name_or_sign_on_roadside}
                      </UncontrolledTooltip>
                    </label>
                    <InputIcon
                      name="name"
                      value={data.name || ""}
                      type="text"
                      placeholder="Sede azienda"
                      onChange={({ target }) => {
                        handleChange(target.name, target.value, "name");
                      }}
                      icon={faCity}
                      disabled={action === "details"}
                      error={getFieldError(error, "name")}
                    />
                  </div>
                </div>
              )}
              <div className="row gx-2">
                <div className="col-12 col-md-6 mb-1">
                  <label className="label-2">
                    {dictionary.registries.email}
                  </label>
                  <InputIcon
                    name="email"
                    value={data.email || ""}
                    type="text"
                    placeholder={action !== "details" ? "email@allabox.it" : ""}
                    onChange={({ target }) => {
                      handleChange(target.name, target.value, "email");
                    }}
                    icon={faAt}
                    disabled={action === "details"}
                    error={getFieldError(error, "email")}
                  />
                </div>
                <div className="col-12 col-md-6 mb-1">
                  <label className="label-2">
                    {dictionary.registries.phone}
                  </label>
                  <InputIcon
                    name="phone"
                    value={data.phone || ""}
                    type="text"
                    placeholder={action !== "details" ? "3999999999" : ""}
                    onChange={({ target }) => {
                      handleChange(target.name, target.value, "phone");
                    }}
                    icon={faPhoneAlt}
                    disabled={action === "details"}
                    error={getFieldError(error, "phone")}
                  />
                </div>
              </div>
              {billing?.enabled &&
                !billingAddressBookIsDifferentFromTheAddressBook && (
                  <>
                    <h3 className="mb-2">{dictionary.words.billing}</h3>
                    <div className="row gx-2">
                      <div className="col-12 col-md-6 col-lg-4 col-xl-3 mb-1">
                        <label className="label-2">
                          {dictionary.registries.vat_number}
                        </label>
                        <Input
                          name="vatNumber"
                          value={data.vatNumber || ""}
                          type="text"
                          placeholder={
                            action !== "details" ? "86334519757" : ""
                          }
                          onChange={({ target }) => {
                            handleChange(
                              target.name,
                              target.value,
                              "vatNumber",
                            );
                          }}
                          disabled={action === "details"}
                          error={getFieldError(error, "vatNumber")}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-4 col-xl-3 mb-1">
                        <label className="label-2">SDI</label>
                        <Input
                          name="sdi"
                          value={data.sdi || ""}
                          type="text"
                          placeholder={action !== "details" ? "SDI" : ""}
                          onChange={({ target }) => {
                            handleChange(target.name, target.value, "sdi");
                          }}
                          disabled={action === "details"}
                          error={getFieldError(error, "sdi")}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-4 col-xl-3 mb-1">
                        <label className="label-2">PEC</label>
                        <Input
                          name="pec"
                          value={data.pec || ""}
                          type="text"
                          placeholder={
                            action !== "details" ? "pec@allabox.it" : ""
                          }
                          onChange={({ target }) => {
                            handleChange(target.name, target.value, "pec");
                          }}
                          disabled={action === "details"}
                          error={getFieldError(error, "pec")}
                        />
                      </div>
                      <div className="col-12 col-md-6 col-lg-4 col-xl-3 mb-1">
                        <label className="label-2">
                          {dictionary.registries.fiscal_code}
                        </label>
                        <Input
                          name="fiscalCode"
                          value={data.fiscalCode || ""}
                          type="text"
                          placeholder={
                            action !== "details" ? "XXXXXX00X00X000X" : ""
                          }
                          onChange={({ target }) => {
                            handleChange(
                              target.name,
                              target.value,
                              "fiscalCode",
                            );
                          }}
                          disabled={action === "details"}
                          error={getFieldError(error, "fiscalCode")}
                        />
                      </div>
                    </div>
                  </>
                )}
              {action !== "details" &&
                registry?.saveEnabled &&
                billing?.enabled &&
                data.location && (
                  <div className="row gx-2">
                    <div className="col text-end">
                      <span
                        className="span-1 cursor-pointer text-decoration-underline"
                        onClick={saveRegistryAddressBook}
                        title={
                          data.registryId
                            ? `${dictionary.actions.edit} ${
                                dictionary.words.in
                              } ${dictionary.contacts.address_book.toLowerCase()}`
                            : `${dictionary.actions.add} ${
                                dictionary.words.in
                              }  ${dictionary.contacts.address_book.toLowerCase()}`
                        }
                      >
                        <FontAwesomeIcon icon={faAddressBook} />{" "}
                        {data.registryId
                          ? `${dictionary.actions.edit} ${
                              dictionary.words.in
                            } ${dictionary.contacts.address_book.toLowerCase()}`
                          : `${dictionary.actions.add} ${
                              dictionary.words.in
                            }  ${dictionary.contacts.address_book.toLowerCase()}`}
                      </span>
                    </div>
                  </div>
                )}
              {data.id && billing?.enabled && billing?.addressBook?.enabled && (
                <div className="row gx-2 mb-2">
                  <div className="col">
                    <Checkbox
                      name="billing"
                      className={`${
                        action === "details" ? "cursor-default" : ""
                      }`}
                      checked={billingAddressBookIsDifferentFromTheAddressBook}
                      onChange={() => {
                        billing?.addressBook?.onChange();
                        setBillingAddressBookIsDifferentFromTheAddressBook(
                          !billingAddressBookIsDifferentFromTheAddressBook,
                        );
                      }}
                      disabled={action === "details"}
                      label={
                        dictionary.messages.billing_different_from_recipient
                      }
                    />
                    {billingAddressBookIsDifferentFromTheAddressBook && (
                      <AddressBookHandlerProvider
                        id={
                          data.id !== billing?.addressBook?.id
                            ? billing?.addressBook?.id
                            : undefined
                        }
                        autosave={autosave}
                        action={action}
                        service={billing?.addressBook?.service}
                        defaultValue={{
                          name: data?.name,
                          vatNumber: data?.vatNumber,
                          sdi: data?.sdi,
                          pec: data?.pec,
                          fiscalCode: data?.fiscalCode,
                        }}
                        callback={(addressBook) => {
                          if (billing?.addressBook?.onChange) {
                            billing?.addressBook?.onChange(addressBook);
                          }
                        }}
                      >
                        <AddressBookHandler />
                      </AddressBookHandlerProvider>
                    )}
                  </div>
                </div>
              )}

              {action !== "details" && autosave === false && (
                <div className="d-flex mt-2">
                  <ButtonUtility
                    className="w-50 me-2"
                    onClick={() => {
                      if (callback) {
                        callback();
                      }
                    }}
                  >
                    {dictionary.actions.cancel}
                  </ButtonUtility>
                  <ButtonConfirm
                    className="w-50"
                    onClick={() => {
                      saveAddressBook(data as AddressBook, "id", true);
                    }}
                    disabled={loader}
                    loading={loader}
                    icon={false}
                  >
                    {dictionary.actions.save}
                  </ButtonConfirm>
                </div>
              )}
            </React.Fragment>
          )}
        </div>
      </div>

      <Modal isOpen={modalConfig.isOpen} size={modalConfig.size}>
        <ModalBody>
          <div className="row mb-3">
            <div className="col">
              <label className="label-4 text-center">{modalConfig.title}</label>
            </div>
          </div>
          {registry?.searchEnabled &&
            modalConfig.action === "searchInAddressBook" && (
              <div className="row">
                <div className="col-12">
                  <label className="label-2">
                    {dictionary.contacts.search_address_in_address_book}
                  </label>
                  <AsyncSelect
                    name="registryId"
                    optionValue="id"
                    optionLabel="name"
                    value={null}
                    className="mb-2"
                    onSearch={async (
                      name: string,
                      value: AddressBook | AddressBook[] | null,
                    ) => {
                      const option: any = { ...value };
                      const location: Location = {
                        ...data.location,
                        ...option.location,
                        id: data.location?.id,
                        registryId: option.location.id,
                      };

                      const locationSaved =
                        await locationSearchRef.current?.saveLocation(
                          location,
                          null,
                          true,
                          false,
                        );

                      const addressBook: AddressBook = {
                        ...option,
                        location: locationSaved,
                        id: data.id,
                        registryId: option.id,
                      };

                      saveAddressBook(
                        addressBook,
                        name as keyof AddressBook,
                        autosave,
                      );

                      setModalConfig(createModalConfig());
                    }}
                    loadOptions={(inputValue, pagination) => {
                      const params: FetchParam = {
                        inputValue,
                        pagination,
                        sorting: { selector: "name", orderBy: "asc" },
                        search: searchInAddressBookParams || [
                          {
                            selector: "myAddress",
                            value: false,
                            precise: true,
                          },
                          {
                            selector: "ownerId",
                            value: ownerId,
                            precise: true,
                          },
                        ],
                      };
                      return getAllRegistryAddressBook(params);
                    }}
                    isClearable={false}
                    isDisabled={action === "details"}
                    placeholder={dictionary.actions.search}
                    optionStyle={(option) =>
                      option.name + " - " + option.location.label
                    }
                  />
                </div>
                <div className="col-12">
                  <ButtonUtility
                    className="w-50"
                    onClick={() => {
                      setModalConfig(createModalConfig());
                    }}
                  >
                    {dictionary.actions.cancel}
                  </ButtonUtility>
                </div>
              </div>
            )}
        </ModalBody>
      </Modal>
    </React.Fragment>
  );
};
